import { mapActions, mapState } from 'pinia';
import Form from '../etc/forms/Form';
import * as bulkApplicationKey from '../services/BulkApplicationKeyService';
import { ADD_NOTIFICATION } from '../state/mutation-types';
import * as contacts from '../services/ContactService';
import * as signatures from '../services/EmailSignatureService';
import Contact from '../models/Contact';
import { normalizeJson } from '../../shared/helpers';
import moment from 'moment';
import TimelineEvent from "../models/TimelineEvent";
import Document from "../models/Document";
import EmailSignature from '../models/EmailSignature';
import EmailTemplate from "../models/EmailTemplate";
import { useRepo } from "pinia-orm";
import { useMainStore } from "../state/store";
import { useAuthenticationStore } from "../state/modules/authentication";
import { useNotificationsStore } from "../state/modules/notifications";
import { useApplicationsStore } from "../state/modules/applications";

export default {
    props: {
        applications: {
            type: Array,
        },
        stages: {
            type: Array,
            default: () => [],
        },
        requestFilters: {
            type: Object,
            default: () => ({}),
        },
        totalApplications: {
            type: Number,
            default: 0,
        },
        defaultSubject: {
            type: String,
            default: '',
        },
        contact: Contact,
        documents: {
            type: Array,
            default: () => []
        },
        context: {
            type: String,
            validator: (value) => ['jobs', 'applications', 'single'].includes(value),
        }
    },
    data: function() {
        return {
            form: this.generateForm(),
            sendingMail: false,
            bulkApplicationData: null,
            sendingMailToCandidates: null,
            scheduleOption: this.contact?.scheduled_at ? 'custom' : 'now',
            files: [],
            selectedSignature: 0,
            templateType: 'interview',
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale', 'currentCompany']),
        ...mapState(useAuthenticationStore, ['currentCompanyId', 'currentUserId', 'currentUser', 'defaultSignature']),
        bulkCount() {
            if (!this.bulkApplicationData)
                return 0;

            return this.bulkApplicationData.attributes.applications_count
        },
        scheduleOptions() {
            return [
                {
                    key: 'now',
                    title: this.$t('mailer.schedule_options.now.title'),
                    description: this.$t('mailer.schedule_options.now.description'),
                    fn: this.setScheduleNow
                },
                {
                    key: 'tomorrow',
                    title: this.$t('mailer.schedule_options.tomorrow.title'),
                    description: this.$t('mailer.schedule_options.tomorrow.description', { hour: '09:00' }),
                    fn: this.setScheduleTomorrow
                },
                {
                    key: 'next_week',
                    title: this.$t('mailer.schedule_options.next_week.title'),
                    description: this.$t('mailer.schedule_options.next_week.description', { hour: '09:00' }),
                    fn: this.setScheduleNextWeek
                },
                {
                    key: 'custom',
                    title: this.$t('mailer.schedule_options.custom.title'),
                    description: this.$t('mailer.schedule_options.custom.description'),
                    fn: this.setScheduleCustom
                }
            ]
        },
        signatures() {
            return useRepo(EmailSignature).orderBy('created_at').get();
        },
        signatureChoices() {
            return [
                { value: 0, label: this.$t('email_signatures.no_signature_selected') },
                ...this.signatures.map(signature => ({
                    value: signature.id,
                    label: signature.name
                })),
            ];
        },
        signaturePreview() {
            return this.signatures.find(signature => signature.id == this.selectedSignature)?.content;
        },
        active_locale() {
            if (!this.templateLocale) {
                return this.currentCompany.jobsite.active_languages.includes(this.locale)
                    ? this.locale
                    : this.currentCompany.jobsite.active_languages[0];
            }

            return this.templateLocale;
        },
        languages() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: field,
                };
            });

            return languages;
        },
        templates() {
            return useRepo(EmailTemplate)
                .with('attachments')
                .where('type', this.templateType)
                .orderBy('order')
                .get();
        },
        templateChoices() {
            return [
                { value: 0, label: '--- ' + this.$t('general.select') + ' ---' },
                ...this.templates.map(template => ({
                    value: template.id || template.core_template,
                    label: template.name[this.templateLocale],
                })),
            ];
        },
        amountValidEmails() {
            if (this.isBulkBasedOnCount) {
                return this.totalApplications;
            }

            return this.applications.filter(i => i.email).length;
        },
        amountInvalidEmails() {
            if (this.isBulkBasedOnCount) {
                return 0;
            }

            return this.applications.length - this.amountValidEmails;
        },
        isBulkBasedOnCount() {
            return this.applications.length !== this.totalApplications;
        },
        applicationsCount() {
            if (!this.bulkKeyDataForCount) {
                return 0;
            }

            return this.bulkKeyDataForCount.attributes.applications_count;
        },
    },
    methods: {
        ...mapActions(useApplicationsStore, {
            storeUpdateApplications: 'updateApplications',
            storeUpdateSelectedApplications: 'updateSelectedApplications'
        }),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        generateForm() {
            let fields = [
                { name: 'bcc', value: '' },
                { name: 'subject', value: this.defaultSubject },
                { name: 'message', value: '<p></p>' },
                { name: 'attachments', value: [] },
                { name: 'scheduled_at', value: null },
            ];

            if (this.contact) {
                fields.forEach((field, index) => {
                    if (typeof this.contact[field.name] !== 'undefined') {
                        fields[index].value = JSON.parse(JSON.stringify(this.contact[field.name]));
                    }
                });
            }

            return new Form(fields);
        },

        setScheduleNow() {
            this.scheduleOption = 'now';
            this.form.scheduled_at = null;
        },
        setScheduleTomorrow() {
            this.scheduleOption = 'tomorrow';
            this.form.scheduled_at = moment().tz('Europe/Brussels').add(1, 'day').hours(9).minutes(0);
        },
        setScheduleNextWeek() {
            this.scheduleOption = 'next_week';
            this.form.scheduled_at = moment().tz('Europe/Brussels').add(1, 'week').hours(9).minutes(0);
        },
        setScheduleCustom() {
            this.scheduleOption = 'custom';
        },

        getBulkApplicationKey() {
            if (!this.$refs.fileUpload.filesAreValid()) {
                return this[ADD_NOTIFICATION]({
                    type: 'warning',
                    message: this.$t('mailer.warning_upload_in_progress'),
                });
            }

            this.sendingMail = true;

            return this.form.submit(data => bulkApplicationKey.get({
                data: {
                    selection: {
                        applications: this.applications.map(({ id }) => id),
                        stages: this.stages,
                    },
                }
            }, this.requestFilters)).then(data => {
                this.bulkApplicationData = data;
            });
        },
        cancelVerification() {
            this.bulkApplicationData = null;
            this.sendingMail = false;
        },
        performSend() {
            if (this.form.scheduled_at) {
                this.form.scheduled_at = moment(this.form.scheduled_at).utc().format('YYYY/MM/DD HH:mm');
            }

            return this.form.submit(data => contacts.storeBulk({
                data: {
                    bulkApplicationId: this.bulkApplicationData.id,
                    attributes: {
                        bcc: data.bcc,
                        subject: data.subject,
                        message: data.message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : ''),
                        attachments: data.attachments,
                        scheduled_at: data.scheduled_at,
                    },
                    relationships: { user: { data: { id: this.currentUserId } } },
                }
            }))
                .then(result => {
                    if (result?.data?.message === 'started') {
                        return this[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('bulkaction.contact.storebulk.started'),
                        });
                    }
                    let data = normalizeJson(result);
                    data.map(contact => {
                        return contact.documents.forEach(document => {
                            delete document.attachable;
                        });
                    });
                    return useRepo(Contact).save(data);
                })
                .then((inserts) => {
                    this.sendingMail = false;

                    if (!inserts) return;

                    let subject = this.totalApplications > 1
                        ? this.$t('applications.selected_applications')
                        : this.applications[0].fullName;

                    if (this.form.scheduled_at) {
                        return this[ADD_NOTIFICATION]({
                            type: 'notification',
                            message: this.$t('mailer.email_scheduled', { date: moment.utc(this.form.scheduled_at).local().format('DD/MM/YYYY HH:mm') }),
                        });
                    } else {
                        return this[ADD_NOTIFICATION]({
                            type: 'notification',
                            message: this.$tc('mailer.email_success', this.totalApplications, { subject }),
                        });
                    }
                });
        },
        performUpdate() {
            this.sendingMail = true;
            if (this.form.scheduled_at)
                this.form.scheduled_at = moment(this.form.scheduled_at).format('YYYY/MM/DD HH:mm')

            return this.form.submit(data => contacts.update(this.contact.id, {
                data: {
                    attributes: data
                }
            })).then(result => {
                return useRepo(Contact).save(normalizeJson(result))
            }).finally(() => {
                this.sendingMail = false;
            })
        },
        performDelete() {
            this.sendingMail = true;

            return contacts.destroy(this.contact.id)
                .then(() => {
                    // Delete event + documents
                    useRepo(TimelineEvent).where('eventable_type', 'contacts').delete(this.contact.id);
                    useRepo(Document).where('attachable_type', 'contacts').delete(this.contact.id);

                    // Delete contact mail
                    return useRepo(Contact).destroy(this.contact.id);
                })
                .then(() => this.sendingMail = false);
        },
        updateApplications(applications) {
            if (this.context === 'applications') {
                this.storeUpdateApplications({ applications });
            } else if (this.context === 'jobs') {
                applications = applications.map(application => application.id);
                this.storeUpdateSelectedApplications({ applications });
            }
        }
    },
    mounted() {
        signatures.index().then(result => {
            return useRepo(EmailSignature).save(normalizeJson(result.data));
        }).then(() => {
            this.selectedSignature = this.defaultSignature?.id || 0;
        });
    }
}
